import {
  DestroyRef,
  ElementRef,
  inject,
  signal,
  Signal,
  WritableSignal,
} from '@angular/core';
import {
  CartFacade,
  UserDataFacade,
  UserProfileFacade,
} from '@boels/shared-state';
import { faExclamationCircle } from '@fortawesome/free-solid-svg-icons';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import {
  LocalStorageService,
  ModalService,
  ScrollService,
  WindowRef,
} from '@boels/helpers';
import {
  BoelsUserTypeEnum,
  Cart,
  Country,
  Order,
  StorageKey,
  Title,
  UserEnum,
} from '@boels/models';
import { CheckoutContainerData } from '../checkout-container/checkout-container.component';
import { combineLatest, map, of, switchMap } from 'rxjs';

export interface ReviewRentalConditionForm {
  termsAndConditions: FormControl<boolean | null>;
  newsletter: FormControl<boolean | null>;
  remarks: FormControl<string | null>;
}

export interface CheckoutState {
  cart: Cart | Order;
  userType: BoelsUserTypeEnum;
  countries: { countries: Country[] };
  titles?: Title[];
}
export abstract class CheckoutReviewAndConfirmationBase {
  #formBuilder = inject(FormBuilder);
  protected readonly userDataFacade = inject(UserDataFacade);
  protected readonly userProfileFacade = inject(UserProfileFacade);
  protected readonly cartFacade = inject(CartFacade);
  protected readonly scrollService = inject(ScrollService);
  protected readonly elementRef = inject(ElementRef);
  protected readonly winRef = inject(WindowRef);
  protected readonly modalService = inject(ModalService);
  protected readonly localstorageService = inject(LocalStorageService);
  protected readonly destroyRef = inject(DestroyRef);
  protected readonly confirmationCartKey: StorageKey =
    StorageKey.CONFIRMATION_CART;

  protected $checkoutState: WritableSignal<CheckoutState | undefined> =
    signal(undefined);

  protected checkoutMetaData$ = combineLatest([
    this.userDataFacade.userData().pipe(
      map((user) => {
        if (user.type === UserEnum.B2C_AUTHENTICATED)
          return BoelsUserTypeEnum.REGISTERED;
        const localStorageService = inject(LocalStorageService);
        const tempUser = localStorageService.getItem(StorageKey.TEMP_USER_TYPE);

        return tempUser as BoelsUserTypeEnum;
      }),
    ),
    this.userProfileFacade.getCountries(),
  ]).pipe(
    switchMap(([userType, countries]) => {
      if (userType !== BoelsUserTypeEnum.REGISTERED) {
        return this.userProfileFacade.getTitles().pipe(
          map((titles) => ({
            userType,
            countries,
            titles: titles.titles,
          })),
        );
      } else {
        return of({
          userType,
          countries,
          titles: undefined,
        });
      }
    }),
  );

  protected abstract $isCheckoutReview: Signal<boolean>;
  protected abstract $showCutOffTimeError: Signal<boolean | undefined>;
  protected abstract containerData: CheckoutContainerData;

  protected abstract backBtnClicked: () => void;
  protected abstract forwardBtnClicked: () => void;

  protected readonly reviewRentalConditionForm: FormGroup<ReviewRentalConditionForm> =
    this.#formBuilder.group({
      termsAndConditions: new FormControl<boolean>(false, {
        validators: [Validators.requiredTrue],
      }),
      newsletter: new FormControl<boolean>(false),
      remarks: new FormControl<string>(''),
    });
  protected readonly faExclamationCircle = faExclamationCircle;
}
